import React, { useId, useMemo, useState } from "react";

import research from "../assets/research.json";

function ResearchEntry({ entry }) {
  const [isAbstractOpen, setIsAbstractOpen] = useState(false);
  const autoId = useId();
  const abstractId = `abstract-${entry.id ?? autoId}`;
  const abstractText = entry.abstract ?? entry.blurb;
  const hasLinks = Boolean(entry.links?.length);
  const hasInlineNote = Boolean(entry.inlineNote?.text || entry.inlineNote?.italicText);
  const hasAbstract = Boolean(abstractText);

  return (
    <div className="researchEntry">
      <div className="researchTitleRow">
        <span className="researchTitle">{entry.title}</span>
        {entry.year ? <span className="researchYear">{entry.year}</span> : null}
      </div>

      {entry.coauthors?.length ? (
        <div className="researchMeta">
          with{" "}
          {entry.coauthors.map((a, idx) => {
            const isLast = idx === entry.coauthors.length - 1;
            const node = a.url ? (
              <a key={a.name} href={a.url} target="_blank" rel="noopener noreferrer">
                {a.name}
              </a>
            ) : (
              <span key={a.name}>{a.name}</span>
            );
            return (
              <React.Fragment key={a.name}>
                {node}
                {!isLast ? ", " : ""}
              </React.Fragment>
            );
          })}
        </div>
      ) : null}

      {entry.status ? <div className="researchMeta">{entry.status}</div> : null}

      {hasAbstract || hasLinks || hasInlineNote ? (
        <div className="researchActions">
          {hasAbstract ? (
            <button
              type="button"
              className="abstractToggle"
              aria-expanded={isAbstractOpen}
              aria-controls={abstractId}
              onClick={() => setIsAbstractOpen((v) => !v)}
            >
              {isAbstractOpen ? "hide abstract" : "show abstract"}
            </button>
          ) : null}

          {hasAbstract && hasLinks ? <span className="dotSep"> • </span> : null}

          {hasLinks ? (
            <span className="researchLinksInline">
              {entry.links.map((l, idx) => (
                <React.Fragment key={l.label}>
                  <a href={l.url} target="_blank" rel="noopener noreferrer">
                    {l.label}
                  </a>
                  {idx < entry.links.length - 1 ? " • " : ""}
                </React.Fragment>
              ))}
            </span>
          ) : null}

          {hasLinks && hasInlineNote ? <span className="dotSep"> • </span> : null}

          {hasInlineNote ? (
            <span className="researchInlineNote">
              {entry.inlineNote?.text ?? ""}
              {entry.inlineNote?.italicText ? <em>{entry.inlineNote.italicText}</em> : null}
            </span>
          ) : null}
        </div>
      ) : null}

      {abstractText && isAbstractOpen ? (
        <p id={abstractId} className="researchAbstract">
          {abstractText}
        </p>
      ) : null}
    </div>
  );
}

function ResearchSection({ id, title, entries }) {
  if (!entries?.length) return null;
  return (
    <section id={id} className="contentSection">
      <h2 className="sectionHeader">{title}</h2>
      {entries.map((e) => (
        <ResearchEntry key={e.id ?? e.title} entry={e} />
      ))}
    </section>
  );
}

export default function AcademicHome() {
  const combinedWorkingPapers = useMemo(() => {
    const wp = research.sections?.workingPapers ?? [];
    const wip = research.sections?.workInProgress ?? [];
    return [...wp, ...wip];
  }, []);
  const hasResearchIntro = Boolean(research.profile?.researchParagraphs?.length);
  const cheUrl = "https://histecon.fas.harvard.edu/";

  return (
    <div className="academicPage">
      <aside className="sidebar">
        <div className="sidebarInner">
          <h1 className="siteTitle">
            {research.profile?.displayName ?? research.profile?.name ?? "Leo Saenger"}
          </h1>
          {research.profile?.photoUrl ? (
            <img
              className="sidebarPhoto"
              src={research.profile.photoUrl}
              alt={`${research.profile?.name ?? "Leo Saenger"} headshot`}
            />
          ) : null}
          {research.profile?.title ? (
            <div className="sidebarText">{research.profile.title}</div>
          ) : null}
          {research.profile?.affiliation ? (
            <div className="sidebarText">{research.profile.affiliation}</div>
          ) : null}

          <nav className="sidebarNav" aria-label="On-page navigation">
            <div className="sidebarNavLine">
              <a href="#welcome">Welcome</a>
              <span className="sidebarNavSep"> • </span>
              {combinedWorkingPapers.length ? <a href="#working-papers">Working papers</a> : null}
              <span className="sidebarNavSep"> • </span>
              <a href="#contact">Contact</a>
            </div>
          </nav>

          {research.profile?.cvUrl || research.profile?.blogUrl ? (
            <div className="sidebarExternalLinks">
              <div className="sidebarNavLine">
                {research.profile?.cvUrl ? (
                  <a href={research.profile.cvUrl} target="_blank" rel="noopener noreferrer">
                    CV (PDF)
                  </a>
                ) : null}
                {research.profile?.cvUrl && research.profile?.blogUrl ? (
                  <span className="sidebarNavSep"> • </span>
                ) : null}
                {research.profile?.blogUrl ? (
                  <a href={research.profile.blogUrl} target="_blank" rel="noopener noreferrer">
                    Blog
                  </a>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </aside>

      <main className="content">
        <div className="contentInner">
          <section id="welcome" className="contentSection">
            <h2 className="sectionHeader">Welcome!</h2>
            {research.profile?.welcomeParagraphs?.map((p, idx) => {
              const cheLabel = "Center for History and Economics";
              if (p.includes(cheLabel)) {
                const [before, after] = p.split(cheLabel);
                return (
                  <p key={idx}>
                    {before}
                    <a href={cheUrl} target="_blank" rel="noopener noreferrer">
                      {cheLabel}
                    </a>
                    {after}
                  </p>
                );
              }
              return <p key={idx}>{p}</p>;
            })}
          </section>

          {hasResearchIntro ? (
            <section id="research" className="contentSection">
              <h2 className="sectionHeader">Research</h2>
              {research.profile?.researchParagraphs?.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </section>
          ) : null}

          <ResearchSection
            id="working-papers"
            title="Working papers & works in progress"
            entries={combinedWorkingPapers}
          />
          <ResearchSection
            id="publications"
            title="Publications"
            entries={research.sections?.publications}
          />

          <section id="contact" className="contentSection">
            <h2 className="sectionHeader">Contact</h2>
            {research.profile?.contact?.email ? (
              <div className="contactRow">
                <span className="contactLabel">Email</span>{" "}
                <a href={`mailto:${research.profile.contact.email}`}>
                  {research.profile.contact.email}
                </a>
              </div>
            ) : null}
            {research.profile?.contact?.website ? (
              <div className="contactRow">
                <span className="contactLabel">Website</span>{" "}
                <a
                  href={research.profile.contact.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {research.profile.contact.website}
                </a>
              </div>
            ) : null}
            {research.profile?.contact?.twitter ? (
              <div className="contactRow">
                <span className="contactLabel">Twitter</span>{" "}
                <a
                  href={research.profile.contact.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {research.profile.contact.twitterLabel ?? "link"}
                </a>
              </div>
            ) : null}
            {research.profile?.contact?.linkedin ? (
              <div className="contactRow">
                <span className="contactLabel">LinkedIn</span>{" "}
                <a
                  href={research.profile.contact.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {research.profile.contact.linkedinLabel ?? "link"}
                </a>
              </div>
            ) : null}
          </section>
        </div>
      </main>
    </div>
  );
}

